import {
  Badge,
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Portal,
  Spinner,
  Wrap,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { CustomerComplianceDocument } from '@texas/api/endpoints/customersApi';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { Icons } from '@texas/components/shared/Icons';
import { useActiveSession } from '@texas/hooks/useSession';
import { useCustomerComplianceDocumentBranches } from './useCustomerComplianceDocumentBranches';

interface Props {
  customerArchived: boolean;
  customerId: number;
  document: CustomerComplianceDocument;
}

export function CustomerComplianceDocumentBranches({
  customerArchived,
  customerId,
  document,
}: Props) {
  const { t } = useTranslation();
  const { branches } = useActiveSession();
  const {
    apply,
    close,
    error,
    hasChanges,
    isOpen,
    loading,
    open,
    selectedBranchIds,
    selectBranches,
  } = useCustomerComplianceDocumentBranches(customerId, document);

  return (
    <Wrap shouldWrapChildren={true}>
      {document.branches.length > 0 ? (
        document.branches.map((branch) => (
          <Badge
            alignItems="center"
            display="inline-flex"
            h="32px"
            key={branch.id}
            title={`${branch.name} (${branch.identifier})`}
            variant="configuration-reference-selected"
          >
            {branch.name} · {branch.identifier}
          </Badge>
        ))
      ) : (
        <Badge
          alignItems="center"
          display="inline-flex"
          h="32px"
          variant="configuration-reference"
        >
          {t('customerComplianceDocuments.global')}
        </Badge>
      )}
      {!customerArchived && (
        <Menu
          closeOnSelect={false}
          isLazy={true}
          isOpen={isOpen}
          placement="bottom-start"
          variant="entityDirectory"
          onClose={close}
          onOpen={open}
        >
          {document.branches.length > 0 ? (
            <MenuButton
              as={IconButton}
              aria-label={t(
                'customerComplianceDocuments.editBranchAssignments',
                { name: document.name },
              )}
              icon={<Icons.Plus />}
              isDisabled={branches.length === 0}
              size="sm"
              variant="entityDirectorySecondary"
            />
          ) : (
            <MenuButton
              as={Button}
              leftIcon={<Icons.Plus />}
              isDisabled={branches.length === 0}
              size="sm"
              variant="entityDirectorySecondary"
            >
              {t('suppliers.workspace.assign')}
            </MenuButton>
          )}
          <Portal>
            <MenuList>
              <ErrorDetails error={error} />
              <MenuOptionGroup
                title={t('customerComplianceDocuments.branchSelectionSummary', {
                  name: document.name,
                  count: selectedBranchIds.length,
                })}
                type="checkbox"
                value={selectedBranchIds.map(String)}
                onChange={selectBranches}
              >
                {branches.map((branch) => (
                  <MenuItemOption
                    key={branch.id}
                    isDisabled={loading}
                    value={branch.id.toString()}
                  >
                    {branch.name} ({branch.identifier})
                  </MenuItemOption>
                ))}
              </MenuOptionGroup>
              <MenuDivider />
              <MenuItem isDisabled={loading} onClick={close}>
                {t('general.cancel')}
              </MenuItem>
              <MenuItem
                icon={loading ? <Spinner size="xs" /> : <Icons.Checkmark />}
                isDisabled={loading || !hasChanges}
                onClick={apply}
              >
                {t('customerComplianceDocuments.applyBranches')}
              </MenuItem>
            </MenuList>
          </Portal>
        </Menu>
      )}
    </Wrap>
  );
}
